import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import { CalibrationRunner } from '@features/calibration/CalibrationRunner';
import { useMasterScriptsQuery } from '@features/scripts/hooks/useScripts';
import { useEquipmentRunsQuery } from '@features/scripts/hooks/useTestRuns';
import { useEquipmentRecord } from '@api/enterprise/equipment/queries/useEquipmentRecord';
import RobotTicker from './RobotTicker';
import CellGroupPicker from './CellGroupPicker';
import { getInstrumentStateChip } from '../utils';
import { extractApiError } from '../hooks/useLaunchRun';
import type { TestRun } from '@features/scripts/types';
import type { CalibrationRunnerLiveContext } from '@features/calibration/CalibrationRunner';
import type { ExecutionEvent } from '@features/scripts/hooks/useExecutionStatus';
import type { RunInstrument } from '../hooks/useRunInstruments';
import type { StartRunBlockedReason } from '../hooks/useLaunchRun';

type Props = {
  instrument: RunInstrument | null;
  live?: CalibrationRunnerLiveContext | null;
  events?: ExecutionEvent[];
  isRunActive?: boolean;
  isCellAssigned?: boolean;
  isAssignmentLoading?: boolean;
  startBlockedReason?: StartRunBlockedReason | null;
  onAssignScript?: (scriptGlobalId: string) => Promise<void> | void;
  isAssigningScript?: boolean;
  assignedScriptGlobalId?: string | null;
  selectedGroups?: string[];
  onSelectedGroupsChange?: (groups: string[] | undefined) => void;
  selectedPoints?: string[];
  onSelectedPointsChange?: (points: string[] | undefined) => void;
};

const formatRunLabel = (run: TestRun): string => {
  const when = run.startedAt ?? run.createdAt;
  const date = when ? new Date(when).toLocaleString() : '';
  return `${date} — ${run.status}`;
};

const DatasheetPane: React.FC<Props> = ({
  instrument,
  live = null,
  events = [],
  isRunActive = false,
  isCellAssigned = true,
  isAssignmentLoading = false,
  startBlockedReason = null,
  onAssignScript,
  isAssigningScript = false,
  assignedScriptGlobalId = null,
  selectedGroups,
  onSelectedGroupsChange,
  selectedPoints,
  onSelectedPointsChange
}) => {
  const { t } = useTranslation('calibration');

  const equipmentRecord = useEquipmentRecord(
    instrument?.equipmentId ?? '',
    instrument?.companyName ?? undefined,
    !!instrument?.equipmentId
  );
  const equipmentGlobalId = equipmentRecord.data?.globalId ?? null;

  const runsByGlobalId = useEquipmentRunsQuery(equipmentGlobalId);
  const runsByEquipmentId = useEquipmentRunsQuery(
    instrument?.equipmentId && instrument.equipmentId !== equipmentGlobalId
      ? instrument.equipmentId
      : null
  );

  const runs = useMemo(() => {
    const merged = new Map<string, TestRun>();
    for (const run of runsByGlobalId.data?.data ?? []) {
      merged.set(run.id, run);
    }
    for (const run of runsByEquipmentId.data?.data ?? []) {
      if (!merged.has(run.id)) merged.set(run.id, run);
    }
    return Array.from(merged.values()).sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [runsByGlobalId.data, runsByEquipmentId.data]);

  const latestRecordRunId = useMemo(
    () => runs.find(run => !!run.dcRuntimeId)?.id ?? null,
    [runs]
  );

  const [viewedRunId, setViewedRunId] = useState<string | null>(null);
  const [pendingScriptId, setPendingScriptId] = useState('');
  const [assignError, setAssignError] = useState<string | null>(null);

  useEffect(() => {
    setViewedRunId(null);
    setPendingScriptId('');
    setAssignError(null);
  }, [instrument?.woDetailGlobalId]);

  const { data: scriptsResponse } = useMasterScriptsQuery();
  const masterScripts = useMemo(
    () => scriptsResponse?.data ?? [],
    [scriptsResponse]
  );

  const assignedScript = useMemo(
    () =>
      assignedScriptGlobalId
        ? masterScripts.find(
            script => script.globalId === assignedScriptGlobalId
          ) ?? null
        : null,
    [masterScripts, assignedScriptGlobalId]
  );

  const handleAssignScript = useCallback(async () => {
    const script = masterScripts.find(entry => entry.id === pendingScriptId);
    if (!script || !onAssignScript) return;
    setAssignError(null);
    try {
      await onAssignScript(script.globalId);
    } catch (error) {
      setAssignError(
        t('runPage.assignScriptFailed', {
          defaultValue: 'Failed to assign the script: {{reason}}',
          reason: extractApiError(error)
        })
      );
    }
  }, [masterScripts, pendingScriptId, onAssignScript, t]);

  const displayedRunId = viewedRunId ?? latestRecordRunId;
  const displayedRun = runs.find(run => run.id === displayedRunId) ?? null;
  const displayedDcRuntimeId = displayedRun?.dcRuntimeId ?? null;
  const isReadOnly = displayedRunId !== latestRecordRunId;

  if (!instrument) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary">
          {t('runPage.datasheetPlaceholder', {
            defaultValue: 'Select an instrument to view its datasheet.'
          })}
        </Typography>
      </Box>
    );
  }

  const chip = getInstrumentStateChip(instrument, t);
  const isLoadingHistory =
    equipmentRecord.isLoading ||
    runsByGlobalId.isLoading ||
    isAssignmentLoading;

  const canAssignInline =
    !!onAssignScript && !!instrument.cell && instrument.equipmentId !== '';

  const emptyState = !isCellAssigned
    ? {
        severity: 'warning' as const,
        message: t('runPage.noRecordNoScript', {
          defaultValue:
            'No calibration record exists for this instrument, and runs will skip it because no script is assigned. Choose a script to include it in the next run.'
        })
      }
    : startBlockedReason === 'noStation'
      ? {
          severity: 'warning' as const,
          message: t('runPage.noRecordNoStation', {
            defaultValue:
              'No calibration record exists for this instrument yet. Runs can’t start until a robot station is configured in Settings.'
          })
        }
      : startBlockedReason === 'disconnected'
        ? {
            severity: 'warning' as const,
            message: t('runPage.noRecordDisconnected', {
              defaultValue:
                'No calibration record exists for this instrument yet. The robot connection is offline — reconnect to start a run.'
            })
          }
        : {
            severity: 'info' as const,
            message: t('runPage.noRecordEmptyState', {
              defaultValue:
                'No calibration record exists for this instrument yet. Start a run to create one.'
            })
          };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 2, sm: 3 }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <Chip
          label={instrument.cell ?? '—'}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace' }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {instrument.equipmentId ||
              t('runPage.unknownEquipment', { defaultValue: 'Unknown' })}
          </Typography>
          {(instrument.description || instrument.companyName) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {instrument.description ?? instrument.companyName}
            </Typography>
          )}
        </Box>
        <Chip
          label={chip.label}
          size="small"
          color={chip.color}
          variant="outlined"
        />
        {runs.length > 0 && (
          <FormControl size="small" sx={{ ml: 'auto', minWidth: 240 }}>
            <Select
              value={displayedRunId ?? ''}
              onChange={e => setViewedRunId(e.target.value)}
              displayEmpty
              renderValue={value => {
                const run = runs.find(r => r.id === value);
                return run
                  ? formatRunLabel(run)
                  : t('runPage.historyLabel', { defaultValue: 'History' });
              }}
            >
              {runs.map(run => (
                <MenuItem
                  key={run.id}
                  value={run.id}
                  disabled={!run.dcRuntimeId}
                >
                  {formatRunLabel(run)}
                  {!run.dcRuntimeId &&
                    ` — ${t('runPage.historyNoDatasheet', {
                      defaultValue: 'no datasheet'
                    })}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {!isRunActive && assignedScript && onSelectedGroupsChange && (
        <CellGroupPicker
          scriptId={assignedScript.id}
          selectedGroups={selectedGroups}
          onGroupsChange={onSelectedGroupsChange}
          selectedPoints={selectedPoints}
          onPointsChange={onSelectedPointsChange}
        />
      )}

      {isRunActive && <RobotTicker events={events} />}

      {displayedDcRuntimeId ? (
        <>
          {isReadOnly && (
            <Alert severity="info">
              {t('runPage.readOnlyHistory', {
                defaultValue:
                  'Viewing a past run — this datasheet is read-only.'
              })}
            </Alert>
          )}
          <CalibrationRunner
            dcRuntimeId={displayedDcRuntimeId}
            live={!isReadOnly ? live ?? undefined : undefined}
            readOnly={isReadOnly}
          />
        </>
      ) : (
        !isLoadingHistory && (
          <>
            <Alert severity={emptyState.severity}>{emptyState.message}</Alert>
            {!isCellAssigned && canAssignInline && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel id="datasheet-assign-script-label">
                    {t('runPage.scriptPickerLabel', {
                      defaultValue: 'Script'
                    })}
                  </InputLabel>
                  <Select
                    labelId="datasheet-assign-script-label"
                    label={t('runPage.scriptPickerLabel', {
                      defaultValue: 'Script'
                    })}
                    value={pendingScriptId}
                    onChange={e => setPendingScriptId(e.target.value)}
                  >
                    {masterScripts.map(script => (
                      <MenuItem key={script.id} value={script.id}>
                        {script.globalId}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAssignScript}
                  disabled={!pendingScriptId || isAssigningScript}
                >
                  {isAssigningScript
                    ? t('runPage.assigningScript', {
                        defaultValue: 'Assigning…'
                      })
                    : t('runPage.assignScript', {
                        defaultValue: 'Assign script'
                      })}
                </Button>
                {assignError && (
                  <Typography variant="caption" color="error">
                    {assignError}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )
      )}
    </Box>
  );
};

export default DatasheetPane;
